// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductListActions from "@adl/Overrides/Widgets/ProductList/ProductListActions";
import ProductListProductInformation from "@adl/Overrides/Widgets/ProductList/ProductListProductInformation";
import { avoidBreakInsidePrintElement, xScreen } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductCardStyles {
    sponsoredText?: TypographyPresentationProps;
    container?: InjectableCss;
    image?: InjectableCss;
    infoSection?: InjectableCss;
    rightSection?: InjectableCss;
    topRightSection?: InjectableCss;
    qty?: InjectableCss;
    price?: InjectableCss;
    fullPrice?: InjectableCss;
    total?: InjectableCss;
    peripheralActions?: InjectableCss;
}

export const productCardStyles: ProductListProductCardStyles = {
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            width: 100%;
            ${avoidBreakInsidePrintElement}
            ${xScreen("lt", "md")} {
                padding: 20px 0;
            }
        `,
    },
    image: {
        css: css`
            width: 120px;
            ${xScreen("lte", "xs")} {
                margin: 0 auto 15px;
            }
            ${xScreen("lte", "md")} {
                margin-bottom: 15px;
            }
        `,
    },
    infoSection: {
        css: css`
            width: calc(100% - 255px - 120px);
            padding-right: 20px;
            padding-left: 12px;
            a[data-test-selector="productDescriptionLink"] {
                margin-bottom: 8px;
            }
        `,
    },
    rightSection: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            width: 255px;
        `,
    },
    topRightSection: {
        css: css`
            display: flex;
            justify-content: space-between;
            ${xScreen("lte", "md")} {
                margin-bottom: 15px;
            }
        `,
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {
    return (
        <StyledWrapper {...styles.container} data-test-selector={`productListProductCard${props.product.id}`}>
            <StyledWrapper {...styles.image}>
                <ProductListProductImage showImage={props.showImage} showCompare={false} />
            </StyledWrapper>
            <StyledWrapper {...styles.infoSection}>
                <ProductListProductInformation
                    showBrand={props.showBrand}
                    showTitle={props.showTitle}
                    showPartNumbers={props.showPartNumbers}
                    showAvailability={props.showAvailability}
                    showAttributes={props.showAttributes}
                    showPreviouslyPurchased={true}
                    showImageBadges={props.showImageBadges}
                    maxImageBadges={props.maximumNumberOfImageBadges}
                    showTextBadges={props.showTextBadges}
                    maxTextBadges={props.maximumNumberOfTextBadges}
                />
                {props.product.isSponsored && (
                    <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.rightSection}>
                <StyledWrapper {...styles.topRightSection}>
                    <ProductListActions
                        showPrice={props.showPrice}
                        showAddToList={props.showAddToList}
                        showCompare={props.showCompare}
                        showImageBadge={props.showImageBadges}
                        showTextBadge={props.showTextBadges}
                    />
                </StyledWrapper>
                <ProductSearchScoreData />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProduct(ProductListProductCard);
